import { constants } from "@/lib/CONSTANTS.js";
import { dateFormat } from "@/common/utils/generic/dateHelper.js";

/**
 * replaceAllMatchPattern
 * @description: Replaces the multiple matching pattern with the actual values
 * @param str Actual string with pattern to be replaced
 * @param mapItems Object with multiple values
 * @returns string with actual values replaced with the item matched
 */
const replaceAllMatchPattern = (str, mapItems) => {
  let regexPattern = Object.keys(mapItems).length > 0 ? new RegExp(Object.keys(mapItems).join("|"), "gi") : null;
  return regexPattern
    ? str.replace(regexPattern, matched => {
        return mapItems[matched];
      })
    : str;
};

// Convert SN from utc to et and sort by date and time
const formatAndSortData = (data, queryParams) => {
  if (data && Array.isArray(data)) {
    data.forEach(item => {
      if (item.name !== "Approved" && item.name !== "Rejected") {
        if (item.createTime) {
          // Converting UTC to ET
          const convertedTime = dateFormat(item.createTime, "yyyy-MM-dd HH:mm:ss", "America/New_York", "utc");
          item.createTime = convertedTime.replace(" ", "T");
        }
      }
    });

    // Sort by createTime based on sortByDirection (creates new array)
    const sortedData = [...data].sort((a, b) => {
      const dateA = new Date(a.createTime || 0);
      const dateB = new Date(b.createTime || 0);

      if (queryParams.sortByDirection === "ASC") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });

    return sortedData;
  }
  return data;
};

/**
 * constructEndpoint
 * @description used for creating endpoint url by calling the replaceAllMatchPattern function
 * @param url string
 * @param mapKeys object with multiple patterns
 * @returns string
 */
const constructEndpoint = (url, mapKeys) => {
  return replaceAllMatchPattern(url, mapKeys);
};

/**
 * queryParamGenerator
 * @description: Loops through the object and construct query param
 * @param object Object parameters
 * @returns string with actual values replaced as ?key1=value1&key2=value2...
 */
const queryParamGenerator = params => {
  const length = Object.keys(params).length;
  let index = 0;
  let query = length === 0 ? "" : "?";
  for (let param in params) {
    query = `${query}${index !== 0 ? "&" : ""}${param}=${encodeURIComponent(params[param])}`;
    index++;
  }
  return query;
};

/**
 * queryParamGeneratorWithoutEncoding
 * @description: Loops through the object and construct query param
 * @param object Object parameters
 * @returns string with actual values replaced as ?key1=value1&key2=value2...
 */
const queryParamGeneratorWithoutEncoding = params => {
  const length = Object.keys(params).length;
  let index = 0;
  let query = length === 0 ? "" : "?";
  for (let param in params) {
    query = `${query}${index !== 0 ? "&" : ""}${param}=${params[param]}`;
    index++;
  }
  return query;
};

/**
 * generateKeyValueFromArray
 * @description: create key value pair object from Array
 * @param keyArray array that hold key for new object
 * @param actualArray array that hold original value
 * @returns object as key value pairs
 */
const generateKeyValueFromArray = (keyArray, actualArray) => {
  let obj = {};
  for (let i = 0; i < actualArray.length; i++) {
    obj[keyArray[i]] = actualArray[i];
  }
  return obj;
};

const encodeURI = data => {
  return encodeURIComponent(data);
};

const dataTableUserAction = params => {
  const { event, findClosestParent = false, buttonLabels = "" } = params;

  if (findClosestParent) {
    event.target.closest(".data-table__rowgroup").classList.add("selected"); //find ancestor parent node that first matches
  } else {
    event.target.parentElement.classList.add("selected"); //find immediate ancestor node
  }
  event.target.classList.add("button--disabled");
  if (buttonLabels) {
    event.target?.textContent?.toLowerCase() === buttonLabels?.text?.toLowerCase() &&
      (event.target.textContent = buttonLabels.toggleText);
  }
};

const dataTableGrAction = params => {
  const { event, findClosestParent = false } = params;

  if (findClosestParent) {
    if (event.target.closest(".gc-5").classList.contains("selected")) {
      event.target.closest(".gc-5").classList.remove("selected");
    } else {
      event.target.closest(".gc-5").classList.add("selected");
    } //find ancestor parent node that first matches}
  } else {
    if (event.target.parentElement.classList.contains("selected")) {
      event.target.parentElement.classList.remove("selected");
    } else {
      event.target.parentElement.classList.add("selected");
    } //find immediate ancestor node
  }
};

const dataTableSubGrAction = params => {
  const { event } = params;
  if (event.target.parentElement.parentElement.classList.contains("selected")) {
    event.target.parentElement.parentElement.classList.remove("selected");
  } else {
    event.target.parentElement.parentElement.classList.add("selected");
  } //find ancestor node
};

const triggerMailToAction = (mailerData, _this) => {
  const { subject = "", body = "" } = mailerData;
  const mailTemplate = `mailto:?subject=${encodeURI(subject)}&body=${encodeURI(body)}`;

  if (mailTemplate.length > 2047) {
    _this.$emit("triggerMessage", "EMAIL_SENT_FAIL");
  } else {
    window.location.href = mailTemplate;
  }
};

const checkInValidFile = (filename, fileTypes) => {
  let parts = filename.split(".");
  let ext = parts[parts.length - 1];
  return !fileTypes.includes(ext);
};

const convertToLowerCase = str => {
  return typeof str === "string" ? str.toLowerCase() : "";
};

const convertToTitleCase = str => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

const isExportManifest = manifestType => constants.pageSettings.manifest.filters.exportManifests.includes(manifestType);
/**
 * convertToSortedData
 * @description: Sorts an array of objects alphabetically by a specified property in ascending order.
 *               Creates a new array without mutating the original data. Handles case-insensitive sorting
 *               and safely manages objects that may not have the specified property.
 * @param {Array} data - Array of objects to be sorted
 * @param {string} [labelKey='label'] - The property name to sort by (defaults to 'label')
 * @returns {Array} - New array with objects sorted alphabetically by the specified property
 * @example
 * // Default usage (sorts by 'label' property)
 * const data = [
 *   { label: "Zebra", id: 1 },
 *   { label: "apple", id: 2 },
 *   { label: "Banana", id: 3 }
 * ];
 * const sorted = convertToSortedData(data);
 * // Result: [{ label: "apple", id: 2 }, { label: "Banana", id: 3 }, { label: "Zebra", id: 1 }]
 *
 * // Custom property usage
 * const vessels = [
 *   { name: "Zebra Ship", id: 1 },
 *   { name: "apple Vessel", id: 2 },
 *   { name: "Banana Boat", id: 3 }
 * ];
 * const sortedVessels = convertToSortedData(vessels, 'name');
 * // Result: [{ name: "apple Vessel", id: 2 }, { name: "Banana Boat", id: 3 }, { name: "Zebra Ship", id: 1 }]
 *
 * // Multiple property fallback
 * const ports = [
 *   { portName: "Zebra Port", id: 1 },
 *   { portCode: "APL", id: 2 },
 *   { portName: "Banana Port", id: 3 }
 * ];
 * const sortedPorts = convertToSortedData(ports, ['portName', 'portCode', 'name']);
 * // Will sort by 'portName' if available, otherwise 'portCode', otherwise 'name'
 */
const convertToSortedData = (data, labelKey = "label") => {
  if (!data || !Array.isArray(data)) {
    return [];
  }

  const sortedData = [...data].sort((a, b) => {
    let valueA = "";
    let valueB = "";

    // Handle array of possible label keys (fallback mechanism)
    if (Array.isArray(labelKey)) {
      for (const key of labelKey) {
        if (a[key] && typeof a[key] === "string") {
          valueA = a[key];
          break;
        }
      }
      for (const key of labelKey) {
        if (b[key] && typeof b[key] === "string") {
          valueB = b[key];
          break;
        }
      }
    } else {
      // Handle single label key
      valueA = a[labelKey] || "";
      valueB = b[labelKey] || "";
    }

    // Convert to uppercase for case-insensitive comparison
    const labelA = valueA.toString().toUpperCase();
    const labelB = valueB.toString().toUpperCase();

    if (labelA < labelB) return -1;
    if (labelA > labelB) return 1;
    return 0;
  });

  return sortedData;
};

export {
  replaceAllMatchPattern,
  formatAndSortData,
  constructEndpoint,
  queryParamGenerator,
  queryParamGeneratorWithoutEncoding,
  generateKeyValueFromArray,
  dataTableUserAction,
  dataTableGrAction,
  dataTableSubGrAction,
  triggerMailToAction,
  convertToLowerCase,
  convertToTitleCase,
  checkInValidFile,
  encodeURI,
  isExportManifest,
  convertToSortedData
};
